import { Injectable } from '@angular/core';

import { Action, Store } from '@ngrx/store';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import * as fromSharedAccountAction from '@jepto/shared/store/account/actions/account.action';
import * as fromSharedAccountModel from '@jepto/shared/store/account/models/account.model';

import { JepCurrentAccountService } from '@jepto/shared/services/jep-current-account/jep-current-account.service';
import { JepRequestService } from '@jepto/shared/services/jep-request/jep-request.service';

import { of, from } from 'rxjs';
import {
	map,
	switchMap,
	delay,
	withLatestFrom,
	filter,
	catchError,
	tap,
} from 'rxjs/operators';

import {
	sentryLogEvent,
	sentryLogError,
	sentryLogSuccess,
} from '@jepto/shared/utils/collection';
import { environment } from '@jepto/environments/environment';

import getAccountQuery from '@jepto/features/settings/graphql/account/queries/query-account';
import updateAccount from '@jepto/features/settings/graphql/account/mutations/mutation-updateAccount';
import {
	isLegacyIanaTimezone,
	normalizeIanaTimezone,
} from '@framework/utils/iana-timezone-location-list';

@Injectable()
export class AccountEffects {
	constructor(
		private actions$: Actions,
		private jepCurrentService: JepCurrentAccountService,
		private appsync: JepRequestService,
		private store: Store<{ account: fromSharedAccountModel.IAccountState }>
	) {}

	readAccount$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromSharedAccountAction.AccountActionType.Read),
				withLatestFrom(this.store.select(state => state.account)),
				filter(([_, state]) => {
					const success = state?.success ?? false;
					const transacting = state?.transacting ?? false;
					console.log('AccountEffects[readAccount]: ', state);
					return !transacting;
				}),
				switchMap(([action, _]) => {
					return from(this.appsync.execute(getAccountQuery, {})).pipe(
						switchMap((result: any) => {
							if (result?.errors && result.errors[0]) {
								sentryLogError(JSON.stringify(result));
								return of(
									new fromSharedAccountAction.ReadError(
										this.getReadErrorPayload()
									)
								);
							}

							const accountData =
								result.data['getItem']['account'];
							if (accountData.status === 'non_renewing') {
								const flag =
									localStorage.getItem('cpl_flag');
								if (Boolean(flag) && flag === '1')
									localStorage.removeItem('cpl_flag');
							}
							sentryLogSuccess(JSON.stringify(result));

							const normalizedTimezone = normalizeIanaTimezone(
								accountData.timezone
							);
							const shouldMigrateLegacyTimezone =
								isLegacyIanaTimezone(accountData.timezone);
							const normalizedAccount = shouldMigrateLegacyTimezone
								? {
										...accountData,
										timezone: normalizedTimezone,
									}
								: accountData;
							const readSuccess =
								new fromSharedAccountAction.ReadSuccess(
									this.getReadSuccessPayload(normalizedAccount)
								);

							if (!shouldMigrateLegacyTimezone) {
								return of(readSuccess);
							}

							return this.appsync
								.mutate({
									mutation: updateAccount,
									variables: {
										input: {
											name: accountData.name,
											domain: accountData.domain,
											timezone: normalizedTimezone,
											weekStart: accountData.weekStart,
											sortKey: accountData.sortKey,
											revisions:
												accountData.revisions ?? 0,
											userKey:
												this.jepCurrentService.getCognitoUserId(),
										},
									},
								})
								.pipe(
									map(() => readSuccess),
									catchError(error => {
										sentryLogError(JSON.stringify(error));
										return of(readSuccess);
									})
								);
						}),
						catchError(error => {
							sentryLogError(JSON.stringify(error));
							return of(
								new fromSharedAccountAction.ReadError(
									this.getReadErrorPayload()
								)
							);
						})
					);
				})
			),
		{ dispatch: true }
	);

	getReadErrorPayload(): fromSharedAccountModel.IAccountState {
		return {
			data: null,
			transacting: false,
			success: false,
			error: true,
		};
	}

	getReadSuccessPayload(
		data: fromSharedAccountModel.IAccount
	): fromSharedAccountModel.IAccountState {
		return {
			data: data,
			transacting: false,
			success: true,
			error: false,
		};
	}

	clientsRevert$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType<fromSharedAccountAction.RevertSharedAccountState>(
					fromSharedAccountAction.AccountActionType
						.RevertSharedAccountState
				),
				delay(200),
				map(action => action.payload),
				map(
					payload =>
						new fromSharedAccountAction.RevertSharedAccountStateSuccess(
							payload
						)
				)
			),
		{ dispatch: true }
	);
}
